import React, { useState, useMemo } from "react";
import {
  UncontrolledTreeEnvironment,
  Tree,
  TreeItem,
  TreeItemIndex,
  TreeDataProvider,
} from "react-complex-tree";
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  AccountBalance as HubIcon,
  Work as ProjectIcon,
} from "@mui/icons-material";
import { useAps } from "contexts/ApsContext";

interface ApsTreeItem {
  index: string;
  canMove: boolean;
  canRename: boolean;
  hasChildren: boolean;
  children?: string[];
  data: {
    type: "hub" | "project" | "folder" | "item";
    id: string;
    name: string;
    hubId?: string;
    projectId?: string;
    folderId?: string;
    itemId?: string;
  };
  isFolder: boolean;
}

interface ApsFileBrowserProps {
  onFileSelect?: (item: ApsTreeItem) => void;
  onUrnExtracted?: (urn: string, encodedUrn: string, item: ApsTreeItem) => void;
}

export const ApsFileBrowser: React.FC<ApsFileBrowserProps> = ({
  onFileSelect,
  onUrnExtracted,
}) => {
  const {
    getHubs,
    getProjects,
    getTopLevelFolders,
    getFolderContents,
    getItemVersions,
  } = useAps();

  const [items, setItems] = useState<Record<string, ApsTreeItem>>({
    root: {
      index: "root",
      canMove: false,
      canRename: false,
      hasChildren: true,
      children: [],
      data: {
        type: "hub",
        id: "root",
        name: "APS Hubs",
      },
      isFolder: true,
    },
  });

  const [loading, setLoading] = useState(false);
  const [loadingItemId, setLoadingItemId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>(["root"]);
  const [treeVersion, setTreeVersion] = useState(0);

  // Load hubs automatically on mount
  React.useEffect(() => {
    loadHubs();
  }, []);

  const dataProvider: TreeDataProvider = useMemo(
    () => ({
      async getTreeItem(itemId: TreeItemIndex): Promise<TreeItem> {
        return items[itemId];
      },

      async onChangeItemChildren(
        itemId: TreeItemIndex,
        newChildren: TreeItemIndex[]
      ): Promise<void> {
        setItems((prevItems) => ({
          ...prevItems,
          [itemId]: {
            ...prevItems[itemId],
            children: newChildren as string[],
          },
        }));
      },

      onDidChangeTreeData(listener: (changedItemIds: TreeItemIndex[]) => void) {
        // Return a disposable
        return { dispose: () => {} };
      },

      async onRenameItem(item: TreeItem, name: string): Promise<void> {
        // Not implemented for APS
      },
    }),
    [items]
  );

  const loadHubs = async () => {
    try {
      setLoading(true);
      setLoadingItemId("root");
      setError(null);
      const hubs = await getHubs();

      const newItems: Record<string, ApsTreeItem> = { ...items };
      const hubIds: string[] = [];

      hubs.forEach((hub) => {
        const hubItemId = `hub-${hub.id}`;
        hubIds.push(hubItemId);
        newItems[hubItemId] = {
          index: hubItemId,
          canMove: false,
          canRename: false,
          hasChildren: true,
          children: [],
          data: {
            type: "hub",
            id: hub.id,
            name: hub.attributes.name,
            hubId: hub.id,
          },
          isFolder: true,
        };
      });

      newItems.root.children = hubIds;
      setItems(newItems);
      setTreeVersion((v) => v + 1);
    } catch (err: any) {
      setError(err.message || "Failed to load hubs");
    } finally {
      setLoading(false);
      setLoadingItemId(null);
    }
  };

  const loadProjects = async (hubId: string, hubItemId: string) => {
    try {
      setLoading(true);
      setLoadingItemId(hubItemId);
      setError(null);
      console.log("Loading projects for hub:", hubId, "hubItemId:", hubItemId);
      const projects = await getProjects(hubId);
      console.log("Projects received:", projects);

      const newItems: Record<string, ApsTreeItem> = { ...items };
      const projectIds: string[] = [];

      projects.forEach((project) => {
        const projectItemId = `project-${project.id}`;
        projectIds.push(projectItemId);
        newItems[projectItemId] = {
          index: projectItemId,
          canMove: false,
          canRename: false,
          hasChildren: true,
          children: [],
          data: {
            type: "project",
            id: project.id,
            name: project.attributes.name,
            hubId,
            projectId: project.id,
          },
          isFolder: true,
        };
        console.log("Created project item:", projectItemId, project.attributes.name);
      });

      newItems[hubItemId].children = projectIds;
      console.log("Setting children for hub:", hubItemId, "children:", projectIds);
      console.log("New items state:", newItems);
      setItems(newItems);
      setTreeVersion((v) => v + 1);
    } catch (err: any) {
      console.error("Error loading projects:", err);
      setError(err.message || "Failed to load projects");
    } finally {
      setLoading(false);
      setLoadingItemId(null);
    }
  };

  const loadTopLevelFolders = async (
    hubId: string,
    projectId: string,
    projectItemId: string
  ) => {
    try {
      setLoading(true);
      setLoadingItemId(projectItemId);
      setError(null);
      const result = await getTopLevelFolders(hubId, projectId);

      const newItems: Record<string, ApsTreeItem> = { ...items };
      const folderIds: string[] = [];

      result.data.forEach((folder) => {
        const folderItemId = `folder-${folder.id}`;
        folderIds.push(folderItemId);
        newItems[folderItemId] = {
          index: folderItemId,
          canMove: false,
          canRename: false,
          hasChildren: true,
          children: [],
          data: {
            type: "folder",
            id: folder.id,
            name: folder.attributes.displayName || folder.attributes.name,
            hubId,
            projectId,
            folderId: folder.id,
          },
          isFolder: true,
        };
      });

      newItems[projectItemId].children = folderIds;
      setItems(newItems);
      setTreeVersion((v) => v + 1);
    } catch (err: any) {
      setError(err.message || "Failed to load folders");
    } finally {
      setLoading(false);
      setLoadingItemId(null);
    }
  };

  const loadFolderContents = async (
    projectId: string,
    folderId: string,
    folderItemId: string,
    hubId: string
  ) => {
    try {
      setLoading(true);
      setLoadingItemId(folderItemId);
      setError(null);
      const result = await getFolderContents(projectId, folderId);

      const newItems: Record<string, ApsTreeItem> = { ...items };
      const childIds: string[] = [];

      result.data.forEach((child) => {
        if (child.type === "folders") {
          const childFolderItemId = `folder-${child.id}`;
          childIds.push(childFolderItemId);
          newItems[childFolderItemId] = {
            index: childFolderItemId,
            canMove: false,
            canRename: false,
            hasChildren: true,
            children: [],
            data: {
              type: "folder",
              id: child.id,
              name: child.attributes.displayName || child.attributes.name,
              hubId,
              projectId,
              folderId: child.id,
            },
            isFolder: true,
          };
        } else if (child.type === "items") {
          const itemItemId = `item-${child.id}`;
          childIds.push(itemItemId);
          newItems[itemItemId] = {
            index: itemItemId,
            canMove: false,
            canRename: false,
            hasChildren: false,
            children: [],
            data: {
              type: "item",
              id: child.id,
              name: child.attributes.displayName,
              hubId,
              projectId,
              folderId,
              itemId: child.id,
            },
            isFolder: false,
          };
        }
      });

      newItems[folderItemId].children = childIds;
      setItems(newItems);
      setTreeVersion((v) => v + 1);
    } catch (err: any) {
      setError(err.message || "Failed to load folder contents");
    } finally {
      setLoading(false);
      setLoadingItemId(null);
    }
  };

  const handleExpandItem = async (item: TreeItem) => {
    const treeItem = item as ApsTreeItem;
    console.log("handleExpandItem called for:", treeItem);

    // If already has children loaded, don't reload
    if (treeItem.children && treeItem.children.length > 0) {
      console.log("Children already loaded, skipping");
      return;
    }

    const { type, hubId, projectId, folderId } = treeItem.data;
    console.log("Item data:", { type, hubId, projectId, folderId });

    // Root node is already loaded on mount, skip it
    if (type === "hub" && treeItem.index === "root") {
      console.log("Root node, skipping");
      return;
    } else if (type === "hub" && hubId) {
      console.log("Loading projects for hub:", hubId);
      await loadProjects(hubId, treeItem.index as string);
    } else if (type === "project" && hubId && projectId) {
      console.log("Loading folders for project:", projectId);
      await loadTopLevelFolders(hubId, projectId, treeItem.index as string);
    } else if (type === "folder" && projectId && folderId && hubId) {
      console.log("Loading folder contents for:", folderId);
      await loadFolderContents(
        projectId,
        folderId,
        treeItem.index as string,
        hubId
      );
    }
  };

  const handleSelectItems = async (itemIds: TreeItemIndex[]) => {
    console.log("handleSelectItems called with:", itemIds);
    if (itemIds.length === 0) return;

    const itemId = itemIds[0];
    const item = items[itemId];
    console.log("Selected item:", item);

    if (!item) {
      console.log("Item not found in items");
      return;
    }

    // Only process selection for file items, not folders/hubs/projects
    if (item.data.type !== "item") {
      console.log("Item is not a file, skipping. Type:", item.data.type);
      return;
    }

    console.log("Processing file selection:", item.data.name);

    // Call onFileSelect callback
    if (onFileSelect) {
      console.log("Calling onFileSelect callback");
      onFileSelect(item);
    }

    // If it's a file item, extract URN
    if (item.data.type === "item" && item.data.projectId && item.data.itemId) {
      try {
        setLoading(true);
        console.log("Extracting URN for item:", item.data.itemId);
        const result = await getItemVersions(
          item.data.projectId,
          item.data.itemId
        );
        console.log("Item versions result:", result);

        if (result.data.length > 0) {
          const latestVersion = result.data[0];
          const urn =
            latestVersion.relationships?.derivatives?.data?.id ||
            latestVersion.id;
          const encodedUrn = btoa(urn).replace(/=/g, "");

          console.log("✅ Extracted URN:", urn);
          console.log("✅ Encoded URN:", encodedUrn);

          if (onUrnExtracted) {
            console.log("Calling onUrnExtracted callback");
            onUrnExtracted(urn, encodedUrn, item);
          }
        } else {
          console.log("No versions found for item");
        }
      } catch (err: any) {
        console.error("Error extracting URN:", err);
        setError(err.message || "Failed to extract URN");
      } finally {
        setLoading(false);
      }
    }
  };

  const renderItemIcon = (item: TreeItem) => {
    const treeItem = item as ApsTreeItem;
    const isExpanded = expandedItems.includes(item.index);

    switch (treeItem.data.type) {
      case "hub":
        return <HubIcon sx={{ color: "#4169e1", mr: 1 }} />; // Royal blue
      case "project":
        return <ProjectIcon sx={{ color: "#6b7280", mr: 1 }} />; // Gray-blue
      case "folder":
        return isExpanded ? (
          <FolderOpenIcon sx={{ color: "#ff9800", mr: 1 }} />
        ) : (
          <FolderIcon sx={{ color: "#ff9800", mr: 1 }} />
        );
      case "item":
        return <FileIcon sx={{ color: "#757575", mr: 1 }} />;
      default:
        return null;
    }
  };

  const renderItemTitle = ({ item, title, context, info }: any) => {
    const treeItem = item as ApsTreeItem;
    const isExpanded = expandedItems.includes(item.index);
    const isLoading = loadingItemId === item.index;
    
    return (
      <Box display="flex" alignItems="center">
        {renderItemIcon(item)}
        <Typography variant="body2">{treeItem.data.name}</Typography>
        {isLoading && <CircularProgress size={14} sx={{ ml: "1rem"}}/>}
      </Box>
    );
  };

  return (
    <Box sx={{ height: "600px", overflow: "auto" }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Browse and choose the file to view its URN
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <UncontrolledTreeEnvironment
        key={treeVersion}
        dataProvider={dataProvider}
        getItemTitle={(item) => (item as ApsTreeItem).data.name}
        viewState={{
          "aps-tree": {
            expandedItems,
          },
        }}
        onExpandItem={(item) => {
          setExpandedItems([...expandedItems, item.index]);
          handleExpandItem(item);
        }}
        onCollapseItem={(item) => {
          setExpandedItems(expandedItems.filter((id) => id !== item.index));
        }}
        onSelectItems={handleSelectItems}
        canDragAndDrop={false}
        canDropOnFolder={false}
        canReorderItems={false}
        renderItemTitle={renderItemTitle}
      >
        <Tree treeId="aps-tree" rootItem="root" treeLabel="APS File Browser" />
      </UncontrolledTreeEnvironment>
    </Box>
  );
};
